import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Disposition,
  dispositionService,
  MAX_ASSISTANT_DISPOSITIONS,
} from "@/services/dispositionService";
import { toast } from "@/components/ui/use-toast";
import { Loader2, Pencil, Plus, Sparkles, Trash2, X } from "lucide-react";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import DispositionEditor from "./DispositionEditor";

// This assistant's dispositions (assistants.dispositions, voxi-2-0#243). The list lives in the form
// and is saved with the assistant; nothing is shared with other assistants.
export default function DispositionsTab() {
  const { watch, setValue } = useFormContext();
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<Disposition | null>(null);
  // A suggestion opened in the editor to change before it is added.
  const [drafting, setDrafting] = useState<Disposition | null>(null);
  // Suggestions from the agent's prompt, not in the list until added; picked = the keys to add.
  const [suggestions, setSuggestions] = useState<Disposition[]>([]);
  const [picked, setPicked] = useState<string[]>([]);
  const [suggesting, setSuggesting] = useState(false);

  // null means no disposition for this assistant's calls, which is what the server does too.
  const list: Disposition[] = watch("dispositions") ?? [];
  const prompt: string = watch("systemPrompt") ?? "";
  // configuration.dispositions_enabled: off by default. A saved list asks nothing until it is on.
  const enabled = watch("dispositions_enabled") === true;
  const keys = list.map((d) => d.key);
  const full = list.length >= MAX_ASSISTANT_DISPOSITIONS;

  // An empty list is saved as null, never as [].
  const setList = (next: Disposition[]) =>
    setValue("dispositions", next.length > 0 ? next : null, { shouldDirty: true });

  const dropSuggestions = (gone: string[]) => {
    setSuggestions((current) => current.filter((s) => !gone.includes(s.key)));
    setPicked((current) => current.filter((k) => !gone.includes(k)));
  };

  const openEditor = (d: Disposition | null, draft: Disposition | null = null) => {
    setEditing(d);
    setDrafting(draft);
    setEditorOpen(true);
  };

  // An edited one replaces itself in place; a new one (typed or drafted from a suggestion) goes
  // to the end of the list.
  const handleSaved = (d: Disposition) => {
    if (editing) {
      setList(list.map((x) => (x.key === editing.key ? d : x)));
      return;
    }
    if (full) {
      toast({
        title: "Not added",
        description: `An assistant can have at most ${MAX_ASSISTANT_DISPOSITIONS} dispositions. Remove some first.`,
      });
      return;
    }
    setList([...list, d]);
    if (drafting) dropSuggestions([drafting.key]);
  };

  const remove = (d: Disposition) => setList(list.filter((x) => x.key !== d.key));

  const move = (index: number, by: number) => {
    const to = index + by;
    if (to < 0 || to >= list.length) return;
    const next = [...list];
    [next[index], next[to]] = [next[to], next[index]];
    setList(next);
  };

  const suggest = async () => {
    setSuggesting(true);
    try {
      const rows = await dispositionService.suggestDispositions(prompt, list);
      const fresh = rows.filter((s) => !keys.includes(s.key));
      setSuggestions(fresh);
      setPicked(fresh.map((s) => s.key));
      if (fresh.length === 0) {
        toast({
          title: "Nothing new to suggest",
          description: "The current list already covers this prompt.",
        });
      }
    } catch (error: any) {
      toast({
        title: "Could not suggest dispositions",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setSuggesting(false);
    }
  };

  // Adds the picked suggestions to the end of the list, up to the limit.
  const addPicked = () => {
    const room = MAX_ASSISTANT_DISPOSITIONS - list.length;
    const toAdd = suggestions.filter((s) => picked.includes(s.key) && !keys.includes(s.key));
    const added = toAdd.slice(0, Math.max(room, 0));
    setList([...list, ...added]);
    dropSuggestions(added.map((s) => s.key));
    if (added.length < toAdd.length) {
      toast({
        title: `${toAdd.length - added.length} not added`,
        description: `An assistant can have at most ${MAX_ASSISTANT_DISPOSITIONS} dispositions. Remove some first.`,
      });
    }
  };

  const keyBadge = (key: string) => (
    <Badge variant="outline" className="bg-secondary text-foreground border-border font-mono">
      {key}
    </Badge>
  );

  const iconButton = (label: string, onClick: () => void, icon: JSX.Element, disabled = false) => (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-7 w-7"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
    >
      {icon}
    </Button>
  );

  const row = (d: Disposition, index: number) => (
    <li key={d.key} className="flex items-start gap-3 px-4 py-3">
      <span className="mt-0.5 w-5 text-right text-sm tabular-nums text-muted-foreground">
        {index + 1}.
      </span>
      <div className="flex-1">
        <span className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground">{d.label}</span>
          {keyBadge(d.key)}
        </span>
        <span className="mt-1 block text-sm text-muted-foreground">{d.description}</span>
      </div>
      <div className="flex gap-1">
        {iconButton(`Move ${d.label} up`, () => move(index, -1), <span aria-hidden>↑</span>, index === 0)}
        {iconButton(
          `Move ${d.label} down`,
          () => move(index, 1),
          <span aria-hidden>↓</span>,
          index === list.length - 1,
        )}
        {iconButton(`Edit ${d.label}`, () => openEditor(d), <Pencil className="h-4 w-4" />)}
        {iconButton(`Remove ${d.label}`, () => remove(d), <Trash2 className="h-4 w-4" />)}
      </div>
    </li>
  );

  const suggestionRow = (s: Disposition) => (
    <li key={s.key} className="flex items-start gap-3 px-4 py-3">
      <Checkbox
        id={`suggestion-${s.key}`}
        checked={picked.includes(s.key)}
        onCheckedChange={(v) =>
          setPicked((current) =>
            v === true ? [...current.filter((k) => k !== s.key), s.key] : current.filter((k) => k !== s.key),
          )
        }
        className="mt-1"
        aria-label={s.label}
      />
      <label htmlFor={`suggestion-${s.key}`} className="flex-1 cursor-pointer">
        <span className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground">{s.label}</span>
          {keyBadge(s.key)}
        </span>
        <span className="mt-1 block text-sm text-muted-foreground">{s.description}</span>
      </label>
      {iconButton(`Edit ${s.label} before adding`, () => openEditor(null, s), <Pencil className="h-4 w-4" />)}
    </li>
  );

  // The same card heading as the Prompt tab, with the two actions on its right.
  return (
    <Card className="border-none shadow-none bg-transparent">
      <CardHeader>
        <CardTitle className="text-foreground">Dispositions</CardTitle>
        <CardDescription>
          What this assistant's calls can conclude. When it is turned on, after each call Voxi
          picks one from the transcript and sends it in the call webhook as{" "}
          <code>disposition</code>; when two fit, the one higher in the list wins.{" "}
          <code>other</code> is always added by Voxi. This does not change how the agent speaks on
          the call.
        </CardDescription>
        <CardAction>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={suggest}
              disabled={suggesting || !prompt.trim()}
              title={prompt.trim() ? undefined : "Write the agent's prompt first"}
            >
              {suggesting ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="mr-1 h-4 w-4" />
              )}
              Suggest from prompt
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => openEditor(null)}
              disabled={full}
            >
              <Plus className="mr-1 h-4 w-4" />
              Add your own
            </Button>
          </div>
        </CardAction>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="flex items-start justify-between gap-4 rounded-md border border-border px-4 py-3">
          <div>
            <label htmlFor="dispositions-enabled" className="text-sm font-medium text-foreground">
              Decide a disposition after each call
            </label>
            <p className="mt-1 text-sm text-muted-foreground">
              {!enabled
                ? "Off: calls get no disposition and no decision request is made. The list below is kept."
                : list.length === 0
                  ? "On, but the list is empty, so calls get no disposition yet. Add at least one."
                  : "On: each call with a transcript gets one of the dispositions below, or other."}
            </p>
          </div>
          <Switch
            id="dispositions-enabled"
            checked={enabled}
            onCheckedChange={(v) => setValue("dispositions_enabled", v === true, { shouldDirty: true })}
          />
        </div>

        {suggestions.length > 0 && (
          <div className="rounded-md border border-primary/40">
            <div className="flex items-center justify-between border-b border-border px-4 py-2">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Suggested from the prompt
              </span>
              {iconButton("Dismiss suggestions", () => dropSuggestions(suggestions.map((s) => s.key)), (
                <X className="h-4 w-4" />
              ))}
            </div>
            <ul className="divide-y divide-border">{suggestions.map(suggestionRow)}</ul>
            <div className="flex items-center justify-end border-t border-border px-4 py-2">
              <Button type="button" size="sm" onClick={addPicked} disabled={picked.length === 0 || full}>
                Add {picked.length} to the list
              </Button>
            </div>
          </div>
        )}

        {list.length === 0 && suggestions.length === 0 ? (
          <p className="rounded-md border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
            No dispositions yet. Suggest some from the agent's prompt, or add your own.
          </p>
        ) : (
          list.length > 0 && (
            <div className="rounded-md border border-border">
              <ul className="divide-y divide-border">{list.map(row)}</ul>
            </div>
          )
        )}

        <p className="text-xs text-muted-foreground">
          {list.length} of {MAX_ASSISTANT_DISPOSITIONS}. Changes are kept when you save the
          assistant. If your system matches on a key, removing it stops that key from being sent.
        </p>
      </CardContent>

      <DispositionEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        editing={editing}
        draft={drafting}
        existingKeys={keys}
        onSaved={handleSaved}
      />
    </Card>
  );
}
